import { useState, Fragment, MouseEvent, ReactNode } from 'react';

import styled from 'styled-components';

import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

export type ShowMoreProps<T = any> = {
  items: T[];
  renderItem: (item: T) => ReactNode;
  initialCount?: number;
  increment?: number;
};

export const ShowMore = <T = any,>({
  items,
  renderItem,
  initialCount,
  increment = 1000,
}: ShowMoreProps<T>) => {
  const { t } = useScopedTranslate(locale);
  const [count, setCount] = useState(initialCount ?? increment);
  const shownItems = items.slice(0, count);

  const onShowMore = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setCount(count + increment);
  };

  return (
    <>
      {shownItems.map((item) => renderItem(item))}
      {count < items.length && (
        <ShowMoreText type="button" onClick={onShowMore}>
          {t('showMore')} ({items.length - count})
        </ShowMoreText>
      )}
    </>
  );
};

const locale = {
  en: {
    showMore: 'Show more',
  },
  is: {
    showMore: 'Sýna meira',
  },
};

const ShowMoreText = styled.button({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  padding: pxToRem(10),
  background: 'none',
  border: 0,
  font: 'inherit',
  color: theme.color.purple60,
  fontSize: pxToRem(12),
  fontWeight: 'bold',
  letterSpacing: pxToRem(0.5),
  textTransform: 'uppercase',
  ':hover': {
    cursor: 'pointer',
    color: theme.color.purple80,
  },
  ':focus-visible': {
    boxShadow: theme.shadow.focus,
    '--shadow-focus-color': theme.color.border100,
  },
});
